import { Icon, Skeleton, Tooltip, Typography } from "@/components/atoms";
import { usePricingProfileContext, useStateRatesContext } from "@/components/organisms";
import { ReactComponent as ACT } from "@/assets/images/config/australian-capital-territory.svg";
import { ReactComponent as NSW } from "@/assets/images/config/new-south-wales.svg";
import { ReactComponent as NT } from "@/assets/images/config/northern-territory.svg";
import { ReactComponent as QLD } from "@/assets/images/config/queensland.svg";
import { ReactComponent as SA } from "@/assets/images/config/south-australia.svg";
import { ReactComponent as TAS } from "@/assets/images/config/tasmania.svg";
import { ReactComponent as VIC } from "@/assets/images/config/victoria.svg";
import { ReactComponent as WA } from "@/assets/images/config/western-australia.svg";
import { clsx } from "@/utils";
import { FixedPricingState } from "@/redux/slices/trip/types";
import { useNavigate } from "react-router-dom";
import { StateRatesEdit } from "./StateRatesEdit";
import { StateRatesView } from "./StateRatesView";
import { Restricted } from "@/components/pages/Restricted/Restricted";

export const StateRates = () => {
  const navigate = useNavigate();
  const { isFetching: isFetchingPricingProfile, isRestricted, pricingProfile } = usePricingProfileContext();
  const { isFetching: isFetchingStateRates, states } = useStateRatesContext();
  const isFetching = isFetchingPricingProfile || isFetchingStateRates;

  if (isFetching) {
    return (
      <div className="flex flex-1 flex-col min-h-[calc(100vh-120px)] px-5 py-4">
        <Skeleton />
      </div>
    );
  }

  if (isRestricted) {
    return (
      <div className="flex flex-1 flex-col min-h-[calc(100vh-120px)] px-5 py-4">
        <Restricted />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col min-h-[calc(100vh-120px)] px-5 py-4">
      <div className="flex flex-col xl:flex-row justify-between gap-4">
        <div className="flex flex-col gap-1">
          <Typography variant="h2">{`${pricingProfile ? `${pricingProfile.name}: ` : ""}State Rates`}</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">Pick a state to set prices by distance range. If no prices are set, the system will apply Custom Rates, then fall back to Default Rates.</Typography>
        </div>
      </div>
      <div className="flex flex-col xl:flex-row items-center xl:items-stretch gap-5 mt-4">
        <ul role="list" className="mt-5 grid w-full gap-5 grid-cols-[repeat(auto-fit,minmax(240px,1fr))]">
          {states.map((state) => {
            const { managed, baseInterval, state: slug, id } = state; 
            const isDefault = !managed && !baseInterval;
            return (
              <li 
                key={id} 
                className={clsx(
                  "min-w-0 flex flex-col p-2.5 gap-1 rounded-lg bg-white border border-neutral-mid-gray", 
                  managed ? "cursor-default" : "cursor-pointer"
                )}
                onClick={() => managed ? null : navigate(slug)}
              >
                <StateDisplay state={state} />
                <Typography variant="action" className="text-neutral-dark-gray">{state.name}</Typography>
                {!isDefault && (
                  <div className="flex flex-row items-center gap-x-1.5 text-neutral-dark-gray">
                    <Icon size="md" name="Setting5" className="flex-shrink-0" />
                    <Typography variant="paragraph">Using Contract Pricing</Typography>
                  </div>
                )}
                {managed ? (
                  <div className="flex">
                    <div>
                      <Tooltip content="Pricing is managed by RideMinder. Please contact RideMinder to make changes" placement="top">
                        <Icon name="Lock1" size="sm" />
                      </Tooltip>
                    </div>
                  </div>
                ) : (
                  <Typography variant="action" className="text-info">
                    {isDefault ? "Set Custom Prices" : "Edit Custom Prices"}
                  </Typography>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

interface StateDisplayProps {
  state: FixedPricingState;
}

const StateDisplay = ({ state }: StateDisplayProps) => {
  const { managed, baseInterval, id } = state;
  const isDefault = !managed && !baseInterval;

  const className = clsx(
    "block w-full h-auto min-h-[150px] shrink-0",
    (managed || isDefault)
      ? "[--state-fill-color:theme(colors.primary.light)] [--state-stroke-color:theme(colors.primary.DEFAULT)]"
      : "[--state-fill-color:theme(colors.orange.light)] [--state-stroke-color:theme(colors.orange.DEFAULT)]"
  );

  switch (id) {
    case "australian-capital-territory":
      return <ACT className={className} />;
    case "new-south-wales":
      return <NSW className={className} />;
    case "northern-territory": 
      return <NT className={className} />;
    case "queensland":
      return <QLD className={className} />;
    case "south-australia":
      return <SA className={className} />;
    case "tasmania":
      return <TAS className={className} />;
    case "victoria":
      return <VIC className={className} />;
    case "western-australia":
      return <WA className={className} />;
    default: 
      return <div className={clsx(className, "rounded-md bg-neutral-gray")} />
  }
}; 

StateRates.View = StateRatesView;
StateRates.Edit = StateRatesEdit;